import type { CreateUserBody, Role, UpdateUserBody } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from './client';

export interface User {
  id: string;
  email: string;
  username: string | null;
  displayName: string;
  role: Role;
  isActive: boolean;
  organisationIds: string[];
  lastLoginAt: string | null;
  lockedUntil: string | null;
  mustChangePassword: boolean;
  /** When they set up their authenticator app; null until their next sign-in. */
  mfaEnabledAt: string | null;
  /** False: an admin turned two-factor off for them. */
  mfaRequired: boolean;
  createdAt: string;
}

export type { Organisation as OrganisationSummary } from './organisations';

const usersKey = ['users'] as const;

export const useUsers = () => useQuery({ queryKey: usersKey, queryFn: () => get<User[]>('/users') });
export { useOrganisations as useOrganisationList } from './organisations';

function useUserMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }) });
}

export const useCreateUser = () => useUserMutation((body: CreateUserBody) => post<User & { welcomeEmailSent: boolean }>('/users', body));
export const useUpdateUser = () => useUserMutation(({ id, ...body }: UpdateUserBody & { id: string }) => patch<User>(`/users/${id}`, body));
export const useDeactivateUser = () => useUserMutation((id: string) => del<User>(`/users/${id}`));
export const useUnlockUser = () => useUserMutation((id: string) => post<User>(`/users/${id}/unlock`));
/** For someone who lost their phone: they set up their authenticator app again at their next sign-in. */
export const useResetTwoFactor = () => useUserMutation((id: string) => post<User>(`/users/${id}/reset-two-factor`));
/** Off: their password alone signs them in. On: they set up their authenticator app at their next sign-in. */
export const useSetTwoFactorRequired = () => useUserMutation(({ id, required }: { id: string; required: boolean }) => post<User>(`/users/${id}/two-factor`, { required }));
export const useSendPasswordReset = () => useUserMutation((id: string) => post<{ message: string }>(`/users/${id}/send-password-reset`));
export const useSendWelcome = () => useUserMutation((id: string) => post<{ message: string }>(`/users/${id}/send-welcome`));
export const useTemporaryPassword = () => useUserMutation((id: string) => post<{ email: string; temporaryPassword: string }>(`/users/${id}/temporary-password`));
